import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Users2, School, Landmark, LineChart, Network } from 'lucide-react';
import Logo from '../components/Logo';
import MoreMenu from '../components/MoreMenu';
import { ROLES, BRAND, LOGIN_URL } from '../lib/mock';
import { useRole } from '../context/RoleContext';
import { useState } from 'react';

const iconMap = { Users2, School, Landmark, LineChart, Network };
const tintMap = {
  royal:  { bg: 'bg-royal-50',  fg: 'text-royal',  arrow: 'text-royal' },
  cyan:   { bg: 'bg-cyan-50',   fg: 'text-cyan',   arrow: 'text-cyan' },
  purple: { bg: 'bg-purple-50', fg: 'text-purple', arrow: 'text-purple' },
  mint:   { bg: 'bg-mint-50',   fg: 'text-mint',   arrow: 'text-mint' },
};

export default function RoleSelection() {
  const navigate = useNavigate();
  const { setRole } = useRole();
  const [moreOpen, setMoreOpen] = useState(false);

  const goTo = (r) => { setRole(r.id); navigate(r.to); };

  return (
    <div className="min-h-screen bg-canvas-radial">
      <header className="absolute inset-x-0 top-0 z-30">
        <div className="mx-auto flex max-w-[1240px] items-center justify-between px-6 py-5">
          <Logo />
          <a href={LOGIN_URL} target="_blank" rel="noopener noreferrer" data-testid="role-selection-login-link" className="text-[13.5px] font-semibold text-navy hover:text-purple transition-colors">Login</a>
        </div>
      </header>

      <div className="mx-auto max-w-[1180px] px-6 pt-32 pb-20">
        {/* Hero — Building future innovators, problem solvers and scientists */}
        <div className="mb-14 rounded-3xl border border-canvas-200 bg-gradient-to-br from-purple-50 via-white to-cyan-50 p-6 md:p-10 shadow-soft">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1.15fr_1fr] items-center">
            <img
              src="https://customer-assets-rejwkqb3.emergentagent.net/job_wix-redesign-2/artifacts/txf3gh75_ChatGPT%20Image%20Jul%2027%2C%202026%2C%2003_56_45%20PM.png"
              alt="Student using VR to explore science and mathematics on Gyan Dhara"
              className="w-full h-auto rounded-2xl"
              loading="eager"
              data-testid="landing-hero-illustration"
            />
            <div>
              <div className="text-[11.5px] font-bold tracking-widest text-purple">GYAN DHARA · VR LEARNING PLATFORM</div>
              <h2 className="mt-3 text-[30px] md:text-[38px] font-extrabold leading-[1.05] tracking-tight text-navy">
                Building future <span className="text-gradient">innovators, problem solvers and scientists.</span>
              </h2>
              <p className="mt-4 max-w-lg text-[14.5px] leading-relaxed text-ink-500">
                Curriculum-aligned VR modules, a connected web learning hub, AI-guided practice and analytics — one immersive learning ecosystem for Grade 8–9 Science and Mathematics.
              </p>
            </div>
          </div>
        </div>

        {/* Promotional video — autoplay muted; users can unmute via YouTube controls */}
        <div className="mb-14 rounded-3xl border border-canvas-200 bg-white p-3 md:p-4 shadow-soft" data-testid="landing-promo-video">
          <div className="relative aspect-video overflow-hidden rounded-2xl bg-black">
            <iframe
              src="https://www.youtube.com/embed/cNg2Jc7sDJ4?autoplay=1&mute=1&playsinline=1&rel=0&modestbranding=1&loop=1&playlist=cNg2Jc7sDJ4"
              title="Gyan Dhara — programme film"
              allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
              allowFullScreen
              loading="eager"
              className="absolute inset-0 h-full w-full border-0"
              data-testid="landing-promo-video-iframe"
            />
          </div>
          <div className="px-2 pt-3 pb-1 text-center text-[11.5px] font-semibold tracking-widest text-purple">GYAN DHARA · PROGRAMME FILM</div>
        </div>

        <div className="text-center">
          <h1 className="mx-auto max-w-4xl text-[40px] md:text-[52px] font-extrabold leading-[1.06] tracking-tight text-navy">
            How would you like to explore <span className="text-gradient">Gyan Dhara?</span>
          </h1>
          <p className="mx-auto mt-5 max-w-xl text-[15px] leading-relaxed text-ink-500">
            Choose the role that best describes you. Your selection personalises the content, while keeping every section accessible.
          </p>
        </div>

        <div className="mt-12 grid grid-cols-1 gap-5 md:grid-cols-3">
          {ROLES.slice(0, 3).map((r) => {
            const Icon = iconMap[r.icon];
            const tint = tintMap[r.color] || tintMap.royal;
            return (
              <button key={r.id} onClick={() => goTo(r)} className="group text-left rounded-2xl border border-canvas-200 bg-white p-6 shadow-soft hover:-translate-y-0.5 hover:shadow-card transition-all">
                <div className="flex items-start justify-between">
                  <span className={`inline-flex h-12 w-12 items-center justify-center rounded-xl ${tint.bg}`}>
                    <Icon className={`h-5 w-5 ${tint.fg} stroke-[1.8]`} />
                  </span>
                </div>
                <h3 className="mt-5 text-[19px] font-bold text-navy leading-snug">{r.title}</h3>
                <p className="mt-2 text-[13.5px] leading-relaxed text-ink-500">{r.desc}</p>
                <div className={`mt-6 inline-flex items-center gap-1 text-[13px] font-semibold ${tint.arrow} group-hover:translate-x-1 transition-transform`}>
                  <ArrowRight className="h-4 w-4" />
                </div>
              </button>
            );
          })}
        </div>
        <div className="mt-5 grid grid-cols-1 gap-5 md:grid-cols-2">
          {ROLES.slice(3).map((r) => {
            const Icon = iconMap[r.icon];
            const tint = tintMap[r.color] || tintMap.purple;
            return (
              <button key={r.id} onClick={() => goTo(r)} className="group text-left rounded-2xl border border-canvas-200 bg-white p-6 shadow-soft hover:-translate-y-0.5 hover:shadow-card transition-all">
                <div className="flex items-start justify-between">
                  <span className={`inline-flex h-12 w-12 items-center justify-center rounded-xl ${tint.bg}`}>
                    <Icon className={`h-5 w-5 ${tint.fg} stroke-[1.8]`} />
                  </span>
                </div>
                <h3 className="mt-5 text-[19px] font-bold text-navy leading-snug">{r.title}</h3>
                <p className="mt-2 max-w-md text-[13.5px] leading-relaxed text-ink-500">{r.desc}</p>
                <div className={`mt-6 inline-flex items-center gap-1 text-[13px] font-semibold ${tint.arrow} group-hover:translate-x-1 transition-transform`}>
                  <ArrowRight className="h-4 w-4" />
                </div>
              </button>
            );
          })}
        </div>

        <div className="mt-10 flex flex-col items-center gap-6">
          <button onClick={() => { setRole('explore'); navigate('/explore'); }} className="rounded-xl border border-canvas-200 bg-white px-6 py-3.5 text-[13.5px] font-semibold text-navy shadow-soft hover:-translate-y-0.5 hover:shadow-card transition-all">
            Explore Gyan Dhara without selecting a role
          </button>
          <div className="text-center text-[12.5px] text-ink-500">
            {BRAND.tagline}
          <div className="mt-4 text-[11.5px]">Conceptualized, designed and developed by {BRAND.developedAt}</div>
          </div>
        </div>
      </div>

      <MoreMenu open={moreOpen} onClose={() => setMoreOpen(false)} />
    </div>
  );
}
